/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/offline/db.ts
 *
 * Rôle :
 *   Wrapper minimal autour d'IndexedDB pour stocker la queue de mutations
 *   hors ligne (cf. CLAUDE.md §18). On utilise l'API native (zéro dépendance)
 *   et on encapsule les promesses pour rendre l'usage agréable depuis du
 *   code async/await moderne.
 *
 *   Schéma actuel (v1) :
 *     - Database : 'tne-offline'
 *     - Object store : 'pending' (keyPath 'id')
 *
 * Réutilisable :
 *   100 %. Wrapper IndexedDB générique — toute autre app DRANE pourrait
 *   le copier-coller, le seul élément spécifique étant le nom de la DB.
 *
 *   Pattern volontairement pas dépendant de la lib `idb` (Jake Archibald)
 *   pour éviter une dep supplémentaire ; si plus tard on a besoin d'API
 *   plus riches (curseurs, transactions multi-store, …), on basculera.
 *
 * @author Mika
 * @since 2026-05-04
 */

/** Nom de la base — versionné si le schéma change (cf. §18.4). */
const DB_NAME = 'tne-offline';
const DB_VERSION = 1;
const STORE_PENDING = 'pending';

/**
 * Ouvre la connexion IndexedDB (singleton).
 * Crée le store `pending` à la première ouverture (onupgradeneeded).
 *
 * @throws si IndexedDB n'est pas disponible (navigateur très ancien) ou
 *         si l'ouverture échoue (mode privé Safari iOS — possible).
 */
let dbPromise: Promise<IDBDatabase> | null = null;

export function openOfflineDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB indisponible dans ce navigateur.'));
  }

  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_PENDING)) {
        const store = db.createObjectStore(STORE_PENDING, { keyPath: 'id' });
        // Index par date de création pour drainer en FIFO.
        store.createIndex('by_created', 'createdAt', { unique: false });
        store.createIndex('by_status', 'status', { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open error'));
    req.onblocked = () =>
      reject(new Error('IndexedDB bloquée — un autre onglet utilise une version antérieure.'));
  });

  return dbPromise;
}

/**
 * Wrap d'une transaction en promesse — facilite l'async/await pour les
 * appels CRUD du queue.ts.
 *
 * @param mode 'readonly' | 'readwrite'
 * @param fn   Callback qui reçoit le store et fait l'opération. Le retour
 *             est résolu dans la promesse.
 */
export async function withPendingStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | Promise<T> | T,
): Promise<T> {
  const db = await openOfflineDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE_PENDING, mode);
    const store = tx.objectStore(STORE_PENDING);
    let result: T | undefined;

    Promise.resolve(fn(store))
      .then((r) => {
        if (r instanceof IDBRequest) {
          r.onsuccess = () => {
            result = r.result;
          };
          r.onerror = () => reject(r.error ?? new Error('IDBRequest error'));
        } else {
          result = r;
        }
      })
      .catch(reject);

    tx.oncomplete = () => resolve(result as T);
    tx.onerror = () => reject(tx.error ?? new Error('Transaction error'));
    tx.onabort = () => reject(tx.error ?? new Error('Transaction aborted'));
  });
}

export const OFFLINE_STORE_PENDING = STORE_PENDING;
